/**
 * The lead table — a thin mapping onto the shared DataTable.
 *
 * Everything generic (sorting, header filters, the column picker, the grid
 * template) moved to `@/shared/components/DataTable` when the events list
 * needed the same behaviour. What is left here is the part that knows what a
 * lead is: the logo, the verify and archive buttons, and the empty-state
 * wording.
 */
import type { ReactNode } from 'react'
import { LeadLogo } from './LeadLogo'
import { DataTable, ColumnPicker as SharedColumnPicker } from '@/shared/components/DataTable'
import type { DataRow, FilterOptions as SharedFilterOptions } from '@/shared/components/DataTable'
import type { Cell, ColumnFilters, ColumnKey, SortDir, SortKey } from '../table'
import { COLUMN_META, FILTERABLE_COLUMNS } from '../table'

export type FilterOptions = SharedFilterOptions<ColumnKey>

export interface TableRow {
  id: string
  name: string
  logoUrl: string
  /** Region · website, under the company name. */
  metaLine: string
  /** Highest flag severity on the lead, or null when it has none. */
  flag: 'critical' | 'warning' | null
  cells: Cell[]
  /** True while the lead is still in the verification step. */
  needsVerifying: boolean
  /** The two lines the phone card shows under the name. */
  cardLines?: (string | false | null | undefined)[]
}

interface Props {
  rows: TableRow[]
  columns: ColumnKey[]
  selectedId: string | null
  onSelect: (id: string) => void
  sortBy: SortKey
  sortDir: SortDir
  onSort: (key: SortKey) => void
  loading: boolean
  search: string
  /** Shown when nothing is visible — say *why*, not "no results". */
  emptyNote?: ReactNode
  filters: ColumnFilters
  filterOptions: FilterOptions
  onToggleFilter: (key: ColumnKey, value: string) => void
  onClearFilter: (key: ColumnKey) => void
  /** Which column's filter dropdown is open, if any. */
  openFilter: ColumnKey | null
  onOpenFilter: (key: ColumnKey | null) => void
  /**
   * Step 0: verify or archive straight from the row.
   *
   * The whole point is not opening the lead — a rep goes down the list deciding
   * yes or no in a second each, and a drawer between every decision would make
   * that the slowest part of the job.
   */
  onVerify: (id: string) => void
  onArchive: (id: string) => void
  busyId: string | null
  /** The open lead's detail, drawn under its own row. */
  expandedId?: string | null
  renderExpanded?: (id: string) => ReactNode
}

export function LeadTable({
  rows,
  columns,
  selectedId,
  onSelect,
  sortBy,
  sortDir,
  onSort,
  loading,
  search,
  emptyNote,
  filters,
  filterOptions,
  onToggleFilter,
  onClearFilter,
  openFilter,
  onOpenFilter,
  onVerify,
  onArchive,
  busyId,
  expandedId,
  renderExpanded,
}: Props) {
  const dataRows: DataRow<ColumnKey>[] = rows.map((row) => ({
    id: row.id,
    title: row.name,
    subtitle: row.metaLine,
    flag: row.flag,
    cells: row.cells,
    card: { lines: row.cardLines },
    leading: <LeadLogo name={row.name} src={row.logoUrl} size={22} radius={6} />,
    actions: row.needsVerifying ? (
      <>
        <button
          className="rowbtn rowbtn--verify"
          disabled={busyId === row.id}
          onClick={() => onVerify(row.id)}
          title={`Accept ${row.name} into the pipeline`}
        >
          Verify
        </button>
        <button
          className="rowbtn rowbtn--archive"
          disabled={busyId === row.id}
          onClick={() => onArchive(row.id)}
          title={`Archive ${row.name} — kept, but out of the pipeline`}
        >
          Archive
        </button>
      </>
    ) : undefined,
  }))

  return (
    <DataTable
      meta={COLUMN_META}
      columns={columns}
      rows={dataRows}
      identityLabel="Company"
      identitySortKey="name"
      sortBy={sortBy}
      sortDir={sortDir}
      onSort={(k) => onSort(k as SortKey)}
      filterable={FILTERABLE_COLUMNS}
      filters={filters}
      filterOptions={filterOptions}
      onToggleFilter={onToggleFilter}
      onClearFilter={onClearFilter}
      openFilter={openFilter}
      onOpenFilter={onOpenFilter}
      selectedId={selectedId}
      onSelect={onSelect}
      expandedId={expandedId}
      renderExpanded={renderExpanded}
      loading={loading}
      loadingNote="Loading leads…"
      emptyNote={
        search.trim()
          ? `No lead matches “${search.trim()}”. Try the company name, a country, or an owner.`
          : (emptyNote ?? 'No leads in the pipeline yet. Import a prospect list, or add one by hand.')
      }
    />
  )
}

export function ColumnPicker({
  columns,
  onToggle,
  onClose,
}: {
  columns: ColumnKey[]
  onToggle: (key: ColumnKey) => void
  onClose: () => void
}) {
  return (
    <SharedColumnPicker
      meta={COLUMN_META}
      columns={columns}
      onToggle={onToggle}
      onClose={onClose}
    />
  )
}
